import React from "react";
import { Box, Chip, Grid2 as Grid, Typography } from "@mui/material";

interface ViewMultiSelectionRowProps {
  values?: string[];
  label?: string;
  placeholder?: string;
  /**
   * Chip size. Defaults to "medium" to match MUI Autocomplete's default tag size,
   * which is what the edit-mode `Autocomplete multiple` renders for selected values.
   */
  size?: "small" | "medium";
  /**
   * If true, the tag row spans full width (xs:12) inside its parent Grid container.
   * Defaults to true to align with the Affiliated Traditions / Languages use cases.
   */
  fullWidth?: boolean;
}

/**
 * Generic read-only pill row component.
 * Renders an array of strings as outlined MUI Chips (matching the visual style of
 * MUI Autocomplete `multiple` selections), but without delete handlers.
 */
const ViewMultiSelectionRow: React.FC<ViewMultiSelectionRowProps> = ({
  values,
  label,
  placeholder = "N/A",
  size = "medium",
  fullWidth = true,
}) => {
  const items = Array.isArray(values) ? values.filter(Boolean) : [];

  const content =
    items.length === 0 ? (
      <Typography variant="body1">{placeholder}</Typography>
    ) : (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          // Mirrors the `margin: 3` MUI applies to `MuiAutocomplete-tag`,
          // producing the same 6px visual spacing between chips as the edit form.
          m: "-3px",
          "& > *": { m: "3px" },
        }}
      >
        {items.map((value, index) => (
          <Chip key={`${value}-${index}`} label={value} size={size} />
        ))}
      </Box>
    );

  return (
    <Grid size={{ xs: 12, sm: fullWidth ? 12 : 6 }}>
      <Box sx={{ mb: 2 }}>
        {label ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            {label}
          </Typography>
        ) : null}
        {content}
      </Box>
    </Grid>
  );
};

export default ViewMultiSelectionRow;
